import { useRef, useCallback, type RefObject } from 'react';

interface SwipeHandlers {
  onSwipeLeft?: () => void;
  onSwipeRight?: () => void;
}

interface SwipeState {
  startX: number;
  startY: number;
  currentX: number;
  isSwiping: boolean;
}

/**
 * Touch-based swipe gesture detection hook.
 * Uses transform: translateX() for 60fps animations.
 */
export function useSwipeGesture(
  elementRef: RefObject<HTMLElement>,
  handlers: SwipeHandlers,
  { threshold = 80, maxVertical = 30 } = {}
) {
  const stateRef = useRef<SwipeState>({ startX: 0, startY: 0, currentX: 0, isSwiping: false });

  const onTouchStart = useCallback((e: React.TouchEvent) => {
    const touch = e.touches[0];
    stateRef.current = {
      startX: touch.clientX,
      startY: touch.clientY,
      currentX: 0,
      isSwiping: false,
    };
  }, []);

  const onTouchMove = useCallback((e: React.TouchEvent) => {
    const touch = e.touches[0];
    const state = stateRef.current;
    const deltaX = touch.clientX - state.startX;
    const deltaY = Math.abs(touch.clientY - state.startY);

    // If vertical scroll exceeds threshold, cancel swipe
    if (deltaY > maxVertical && !state.isSwiping) return;

    if (Math.abs(deltaX) > 10) {
      state.isSwiping = true;
      state.currentX = deltaX;

      // Clamp the transform for visual feedback
      const clamped = Math.max(-120, Math.min(120, deltaX));
      if (elementRef.current) {
        elementRef.current.style.transform = `translateX(${clamped}px)`;
        elementRef.current.style.transition = 'none';
      }
    }
  }, [elementRef, maxVertical]);

  const onTouchEnd = useCallback(() => {
    const state = stateRef.current;

    if (elementRef.current) {
      elementRef.current.style.transform = 'translateX(0)';
      elementRef.current.style.transition = 'transform 0.2s ease-out';
    }

    if (!state.isSwiping) return;

    if (state.currentX < -threshold) {
      handlers.onSwipeLeft?.();
    } else if (state.currentX > threshold) {
      handlers.onSwipeRight?.();
    }

    stateRef.current = { startX: 0, startY: 0, currentX: 0, isSwiping: false };
  }, [elementRef, handlers, threshold]);

  return { onTouchStart, onTouchMove, onTouchEnd };
}
